/* eslint react-hooks/exhaustive-deps: "warn" */

import { useCallback, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';

import styles from './ScreeningChainCard.module.scss';

import type { ScreeningChainCardProps } from './ScreeningChainCard.types';

// Duration (ms) to wait before advancing INTO each step index (1..15).
const STEP_DURATIONS = [
  0, // 0 — idle
  700, // 1 node0 in
  350, // 2 node0 scan on
  950, // 3 node0 tag + rail0
  500, // 4 node1 in
  350, // 5 node1 scan on
  500, // 6 chip 1
  250, // 7 chip 2
  250, // 8 chip 3
  700, // 9 node1 tag + rail1
  500, // 10 node2 in
  350, // 11 node2 scan on
  950, // 12 node2 tag + exposed
  500, // 13 calc
  700, // 14 verdict
  500, // 15 sources + done
];

const LAST_STEP = STEP_DURATIONS.length - 1;

export const ScreeningChainCard = ({
  entityName,
  entityMeta,
  query,
  nodes,
  calc,
  verdict,
  sources,
  note,
  screeningLabel = 'SCREENING',
  exposedLabel = 'EXPOSED',
  searchTitle = 'Screen a group structure',
  searchCta = 'Screen group',
  searchHint = 'entity · directors · shareholders · UBO chain',
  replayLabel = 'Replay screening',
  className,
}: ScreeningChainCardProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersection(ref, { threshold: 0.35 });

  const [typed, setTyped] = useState('');
  const [running, setRunning] = useState(false);
  const [step, setStep] = useState(0);
  const [openNode, setOpenNode] = useState<string | null>(null);

  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  // advance through the screening steps
  const advance = useCallback((next: number) => {
    if (next > LAST_STEP) {
      return;
    }

    const id = setTimeout(() => {
      setStep(next);
      advance(next + 1);
    }, STEP_DURATIONS[next]);

    timers.current.push(id);
  }, []);

  const run = useCallback(() => {
    clearTimers();
    setOpenNode(null);
    setStep(0);
    setRunning(true);
    advance(1);
  }, [advance, clearTimers]);

  // typewriter for the search overlay, then auto-run
  useEffect(() => {
    if (!visible || running || typed === query) {
      return;
    }

    const id = setTimeout(() => setTyped(query.slice(0, typed.length + 1)), 55);
    timers.current.push(id);

    return () => clearTimeout(id);
  }, [visible, running, typed, query]);

  useEffect(() => {
    if (visible && !running && typed === query) {
      const id = setTimeout(run, 550);
      timers.current.push(id);

      return () => clearTimeout(id);
    }
  }, [visible, running, typed, query, run]);

  useEffect(() => clearTimers, [clearTimers]);

  const done = running && step >= LAST_STEP;
  const exposed = step >= 12;

  const startNow = () => {
    setTyped(query);
    run();
  };

  // per-node visibility thresholds: [in, scanStart, tagStart]
  const NODE_STEPS = [
    { in: 1, scanStart: 2, tagStart: 3 },
    { in: 4, scanStart: 5, tagStart: 9 },
    { in: 10, scanStart: 11, tagStart: 12 },
  ];

  return (
    <div className={cn(styles.stage, className)}>
      <div
        ref={ref}
        className={cn(styles.card, running && styles.anim, done && styles.done)}
        aria-label="Interactive demo: screening a group structure for sanctions and PEP exposure"
      >
        <div className={styles.top}>
          <div className={styles.topTitle}>
            {entityName}
            <small>{entityMeta}</small>
          </div>

          <span className={cn(styles.flag, exposed ? styles.flagExposed : styles.flagScreening)}>
            {!exposed && <span className={styles.spin} />}
            {exposed ? exposedLabel : screeningLabel}
          </span>
        </div>

        <div className={styles.body}>
          <div className={styles.chain}>
            <span className={cn(styles.rail, step >= 3 && styles.railOn)} style={{ top: 16, height: 84 }} />
            <span
              className={cn(styles.rail, styles.railRed, step >= 9 && styles.railOn)}
              style={{ top: 100, height: 92 }}
            />

            {nodes.map((node, i) => {
              const s = NODE_STEPS[i] ?? NODE_STEPS[NODE_STEPS.length - 1];
              const isIn = step >= s.in;
              const scanning = step >= s.scanStart && step < s.tagStart;
              const tagShown = step >= s.tagStart;
              const isHit = node.tag.variant === 'red';
              const isOpen = openNode === node.id;
              const chipCount = i === 1 ? Math.max(0, Math.min(step - 5, node.chips?.length ?? 0)) : 0;

              return (
                <div
                  key={node.id}
                  className={cn(
                    styles.node,
                    isIn && styles.nodeIn,
                    isHit && tagShown && styles.nodeHit,
                    isOpen && styles.nodeOpen,
                  )}
                  tabIndex={done ? 0 : undefined}
                  role={done ? 'button' : undefined}
                  onClick={done ? () => setOpenNode(isOpen ? null : node.id) : undefined}
                  onKeyDown={
                    done
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setOpenNode(isOpen ? null : node.id);
                          }
                        }
                      : undefined
                  }
                >
                  <div className={styles.nRow}>
                    <span className={styles.nName}>{node.name}</span>

                    {scanning && (
                      <span className={cn(styles.scanline, styles.scanlineOn)}>
                        <span className={styles.spin} />
                        {node.scanLabel}
                      </span>
                    )}

                    {tagShown && (
                      <span className={cn(styles.tag, styles[`tag_${node.tag.variant}`], styles.tagPop)}>
                        {node.tag.label}
                      </span>
                    )}
                  </div>

                  <div className={styles.nMeta}>{node.meta}</div>

                  {node.chips && node.chips.length > 0 && (
                    <div className={styles.chips}>
                      {node.chips.slice(0, chipCount).map((chip) => (
                        <span key={chip.label} className={cn(styles.chip, styles[`chip_${chip.variant}`])}>
                          <i>{chip.variant === 'clear' ? '✓' : '!'}</i>
                          {chip.label}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className={styles.drawer}>
                    <div className={styles.drawerIn}>
                      {node.drawer.map((row) => (
                        <div key={row.label} className={styles.drawerRow}>
                          <span>{row.label}</span>
                          <b className={row.valueTone ? styles[`val_${row.valueTone}`] : undefined}>{row.value}</b>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={cn(styles.calc, step < 13 && styles.calcOut)}>
            {calc.map((part, i) => (
              <span
                key={i}
                className={cn(
                  step >= 13 && styles.calcOn,
                  part.tone === 'blue' && styles.calcBlue,
                  part.tone === 'red' && styles.calcRed,
                  part.tone === 'muted' && styles.calcMuted,
                )}
              >
                {part.text}
              </span>
            ))}
          </div>

          <div className={cn(styles.verdict, step >= 14 && styles.verdictIn)}>
            <span className={styles.verdictIcon}>!</span>
            <p>{verdict.body}</p>
          </div>
        </div>

        <div className={cn(styles.src, step >= 15 && styles.srcIn)}>
          <i />
          {sources}
        </div>

        {!running && (
          <div className={cn(styles.search, running && styles.searchGone)}>
            <h3>{searchTitle}</h3>

            <div className={styles.bar}>
              <span className={styles.q}>
                {typed}
                <span className={styles.cur} />
              </span>

              <button className={styles.searchBtn} type="button" onClick={startNow}>
                {searchCta} →
              </button>
            </div>

            <div className={styles.hint}>{searchHint}</div>
          </div>
        )}

        {done && (
          <button className={styles.replay} type="button" onClick={run}>
            ↺ {replayLabel}
          </button>
        )}
      </div>

      {note && <div className={cn(styles.note, done && styles.noteIn)}>{note}</div>}
    </div>
  );
};
